import { formatGameResult, type TournamentEvent, type TournamentSettings } from '@ctd/core';
import { byeLabel } from './results';

export interface LogLine {
  readonly seq: number;
  readonly at: string;
  readonly actor: string;
  readonly type: TournamentEvent['type'];
  readonly text: string;
}

const SETTING_LABELS: Partial<Record<keyof TournamentSettings, string>> = {
  name: 'name',
  city: 'city',
  startDate: 'start date',
  endDate: 'end date',
  chiefArbiter: 'chief arbiter',
  rounds: 'rounds',
  system: 'pairing system',
  pointSystem: 'point system',
  initialColour: 'initial colour',
  tiebreaks: 'tiebreaks',
};

function settingText(k: keyof TournamentSettings, v: unknown): string {
  const label = SETTING_LABELS[k] ?? k;
  if (k === 'pointSystem') return `${label} changed`;
  if (Array.isArray(v)) return `${label} ${v.join(', ') || '(none)'}`;
  if (k === 'initialColour') return `${label} ${v === 'b' ? 'black' : 'white'}`;
  return `${label} "${String(v)}"`;
}

/**
 * Describe every event in plain words, in log order. Player names are taken
 * from the log itself (the name at the time of the event).
 */
export function describeEvents(events: readonly TournamentEvent[]): LogLine[] {
  const names = new Map<string, string>();
  const who = (id: string): string => names.get(id) ?? id;
  return events.map((e) => {
    let text: string;
    switch (e.type) {
      case 'TournamentCreated':
        text = `Created "${e.settings.name}": ${e.settings.rounds} rounds, ${e.settings.system}`;
        break;
      case 'SettingsChanged': {
        const parts = (Object.keys(e.patch) as (keyof TournamentSettings)[]).map((k) =>
          settingText(k, e.patch[k]),
        );
        text = `Settings: ${parts.join('; ') || 'no change'}`;
        break;
      }
      case 'PlayerAdded':
        names.set(e.player.id, e.player.name);
        text = `Added ${e.player.name}${e.player.rating > 0 ? ` (${e.player.rating})` : ''}`;
        break;
      case 'PlayerUpdated': {
        const before = who(e.id);
        if (e.patch.name !== undefined) names.set(e.id, e.patch.name);
        const fields = Object.keys(e.patch).join(', ');
        text = `Edited ${before}${fields ? `: ${fields}` : ''}`;
        break;
      }
      case 'PlayerRemoved':
        text = `Removed ${who(e.id)}`;
        break;
      case 'PlayersRenumbered':
        text = `Renumbered ${e.order.length} players (seeding)`;
        break;
      case 'PlayerWithdrawn':
        text = `Withdrew ${who(e.id)} after round ${e.afterRound}`;
        break;
      case 'PlayerReinstated':
        text = `Reinstated ${who(e.id)}`;
        break;
      case 'ByeRequested':
        text = `${byeLabel(e.kind)} requested for ${who(e.id)} in round ${e.round}`;
        break;
      case 'ByeCancelled':
        text = `Cancelled ${who(e.id)}'s bye in round ${e.round}`;
        break;
      case 'RoundPaired':
        text = `Paired round ${e.round} (${e.games.length} boards${
          e.byes.length > 0 ? `, ${e.byes.length} bye${e.byes.length > 1 ? 's' : ''}` : ''
        }) with ${e.engine}`;
        break;
      case 'RoundUnpaired':
        text = `Unpaired round ${e.round}`;
        break;
      case 'RoundEdited':
        text = `Edited the pairing of round ${e.round} by hand`;
        break;
      case 'ResultEntered':
        text = `Round ${e.round}, board ${e.board}: ${
          e.result.white === '*' ? 'result cleared' : formatGameResult(e.result)
        }`;
        break;
      case 'RoundPublished':
        text = `Published round ${e.round}`;
        break;
    }
    return { seq: e.seq, at: e.at, actor: e.actor, type: e.type, text };
  });
}

/** One line for the undo button: what undo would remove. */
export function describeLast(events: readonly TournamentEvent[]): string | null {
  if (events.length <= 1) return null;
  return describeEvents(events).at(-1)?.text ?? null;
}
